import { StateCreator } from "zustand";
import { VendState } from ".";
import { OrganizationMember } from "../types";
import {
  fetchOrganizationMembers,
  updateOrganizationMemberRole as updateOrganizationMemberRoleApi,
  removeOrganizationMember as removeOrganizationMemberApi,
  inviteOrganizationUser as inviteOrganizationUserApi,
  revokeOrganizationInvite as revokeOrganizationInviteApi,
} from "../api/user/api";
import { withWriteAction } from "../utils";
import { runLoad } from "./helpers";
import { useRoutePlannerStore } from "./routePlanner";

let organizationMembersPromise: Promise<void> | null = null;
const ORGANIZATION_MEMBERS_TTL_MS = 5 * 60_000;

export type OrgMembersSlice = {
  organizationMembers: OrganizationMember[];
  organizationMembersLoading: boolean;
  organizationMembersError: string | null;
  organizationMembersFetchedAt: number | null;
  loadOrganizationMembers: (options?: { force?: boolean; ttlMs?: number }) => Promise<void>;
  updateOrganizationMemberRole: (userId: string, role: string) => Promise<void>;
  removeOrganizationMember: (userId: string) => Promise<void>;
  inviteOrganizationUser: (
    email: string,
    role: string,
    firstName?: string,
    lastName?: string
  ) => Promise<void>;
  revokeOrganizationInvite: (userId: string) => Promise<void>;
};

export const createOrgMembersSlice: StateCreator<
  VendState,
  [],
  [],
  OrgMembersSlice
> = (set, get, _store) => ({
  organizationMembers: [],
  organizationMembersLoading: false,
  organizationMembersError: null,
  organizationMembersFetchedAt: null,
  loadOrganizationMembers: async (options) => {
    const state = get();
    const ttlMs = options?.ttlMs ?? ORGANIZATION_MEMBERS_TTL_MS;
    if (
      !options?.force &&
      state.organizationMembersFetchedAt &&
      Date.now() - state.organizationMembersFetchedAt < ttlMs
    ) {
      return;
    }
    if (organizationMembersPromise) return organizationMembersPromise;
    organizationMembersPromise = runLoad({
      isLoading: () => get().organizationMembersLoading,
      setLoading: (loading) =>
        set({ organizationMembersLoading: loading } as VendState),
      setError: (message) =>
        set({ organizationMembersError: message } as VendState),
      fetcher: fetchOrganizationMembers,
      onSuccess: (members) =>
        set({
          organizationMembers: members,
          organizationMembersFetchedAt: Date.now(),
        } as VendState),
      errorMessage: "Failed to load organization members",
    }).finally(() => {
      organizationMembersPromise = null;
    });
    return organizationMembersPromise;
  },
  updateOrganizationMemberRole: async (userId, role) => {
    await withWriteAction("Updating Role", async () => {
      await updateOrganizationMemberRoleApi(userId, role);
      set({ operatorsHydrated: false } as VendState);
      await get().loadOrganizationMembers({ force: true });
    });
  },
  removeOrganizationMember: async (userId) => {
    await withWriteAction("Removing Member", async () => {
      await removeOrganizationMemberApi(userId);
      await get().loadTasks();
      set({ operatorsHydrated: false } as VendState);
      useRoutePlannerStore.getState().reset();
      await get().loadOrganizationMembers({ force: true });
    });
  },
  inviteOrganizationUser: async (email, role, firstName, lastName) => {
    await withWriteAction("Inviting User", async () => {
      await inviteOrganizationUserApi({ email, role, firstName, lastName });
      set({ operatorsHydrated: false } as VendState);
      await get().loadOrganizationMembers({ force: true });
    });
  },
  revokeOrganizationInvite: async (userId) => {
    await withWriteAction("Revoking Invite", async () => {
      await revokeOrganizationInviteApi(userId);
      set({ operatorsHydrated: false } as VendState);
      await get().loadOrganizationMembers({ force: true });
    });
  },
});
